// Normalize external share URLs into iframe-embeddable URLs.
// Returns { url, hint } where hint describes the transformation (or warns if not embeddable).

export const normalizeEmbedUrl = (raw, kind /* 'pdf' | 'video' | 'quiz' */) => {
  if (!raw) return { url: '', hint: '' };
  const url = raw.trim();
  let hint = '';

  // --- Google Drive: /view → /preview
  // https://drive.google.com/file/d/FILE_ID/view?... → /preview
  const gdriveMatch = url.match(/drive\.google\.com\/file\/d\/([^/]+)/);
  if (gdriveMatch) {
    const fileId = gdriveMatch[1];
    return {
      url: `https://drive.google.com/file/d/${fileId}/preview`,
      hint: 'Google Drive: auto-converted to /preview embed URL.',
    };
  }

  // --- YouTube: watch?v= or youtu.be → /embed/
  const ytWatch = url.match(/youtube\.com\/watch\?v=([^&]+)/);
  if (ytWatch) {
    return {
      url: `https://www.youtube.com/embed/${ytWatch[1]}`,
      hint: 'YouTube: auto-converted to /embed URL.',
    };
  }
  const ytShort = url.match(/youtu\.be\/([^?]+)/);
  if (ytShort) {
    return {
      url: `https://www.youtube.com/embed/${ytShort[1]}`,
      hint: 'YouTube: auto-converted to /embed URL.',
    };
  }

  // --- OneDrive: try to detect bad URLs
  if (/1drv\.ms|onedrive\.live\.com|sharepoint\.com/i.test(url)) {
    // SharePoint Business share link pattern: /:v:/p/... or /:b:/p/... or /:w:/p/...
    if (/sharepoint\.com\/:[vbwxpf]:\//i.test(url)) {
      hint =
        '⚠️ SharePoint Business share link detected. SharePoint Business refuses iframe embedding outside Microsoft domains (Teams, PowerBI, etc.) — your students will NOT see the content inline. Recommended fix: download the file and re-upload here using the Upload button.';
      return { url, hint };
    }
    if (!/embed/i.test(url)) {
      hint =
        '⚠️ OneDrive: this looks like a share link, not an embed URL. In OneDrive (personal) open the file → Share → Embed → copy the iframe SRC (it should contain "/embed?"). Plain share links will be refused by the browser.';
    } else {
      hint = 'OneDrive embed URL detected.';
    }
    return { url, hint };
  }

  // --- Microsoft Forms: detect design URL vs response URL
  if (/forms\.office\.com|forms\.microsoft\.com/i.test(url)) {
    if (/DesignPage|subpage=design/i.test(url)) {
      hint =
        '⚠️ This is the MS Forms DESIGN/EDIT URL (where you build the form). Students cannot answer here. You need the RESPONSE/EMBED URL: open the form → Collect responses → set to "Anyone can respond" → Send → Embed → copy the iframe SRC.';
      return { url, hint };
    }
    let out = url;
    if (!/embed=true/i.test(url)) {
      out = url + (url.includes('?') ? '&' : '?') + 'embed=true';
      hint = 'MS Forms: auto-appended &embed=true. If still refused, the form must be set to "Anyone can respond" in Forms settings (not org-only).';
    } else {
      hint = 'MS Forms embed URL detected.';
    }
    return { url: out, hint };
  }

  // --- PDFs hosted on third-party domains that block framing:
  // we leave the raw URL but ModuleViewer will wrap it in a viewer if needed.
  if (kind === 'pdf') {
    hint =
      'Tip: if the PDF is blocked by the browser, upload it via the "Upload file" button — it will be served from this app and always renders.';
  }

  return { url, hint };
};

// For rendering: if it's a PDF served from an external host that may set
// X-Frame-Options, we cannot detect that client-side reliably. We just use the
// URL as-is. Self-hosted /api/files/* URLs render fine.
export const pdfRenderSrc = (rawUrl) => rawUrl || '';
